import React, { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Grid, Typography, TextField, Box } from '@the-platform-group/component-library'
import {
  Modal,
  ModalTitle,
  ModalContent,
  ModalActions,
} from '@the-platform-group/component-library/Modal'
import { Button } from '@the-platform-group/component-library/Button'
import useSecureFileUpload from 'pages/Nobo/components/useSecureFileUpload'
import { PUBLIC_S3_BUCKET_TYPE } from 'constants/fileExtensions'
import AttachFilesButton from 'pages/IrwinAssistant/Requests/components/AttachFilesButton'

const DISPLAY_ORDER_HELPER_TEXT =
  'Controls where this template appears in the template selection list. Ordering is scoped to each template type, so this only ranks it against other templates of the same type - lower numbers appear first.'
const PAGE_COUNT_HELPER_TEXT =
  'Number of pages in the template, displayed to users on template selection screen.'

const NODE_ID_FIELDS = [
  { name: 'pages.reportingHasParent', label: 'Reporting organization with a group' },
  {
    name: 'pages.reportingNoParent',
    label: 'Reporting organization without a group',
  },
  {
    name: 'pages.nonReportingHasParent',
    label: 'Non-reporting organization with a group',
  },
  {
    name: 'pages.nonReportingNoParent',
    label: 'Non-reporting organization without a group',
  },
]

const ITINERARY_NODE_ID_FIELDS = [{ name: 'pages.itinerary', label: 'Node ID' }]

const DEFAULT_VALUES = {
  name: '',
  description: '',
  externalId: '',
  displayOrder: '',
  pageCount: '',
  previewImageUrl: '',
  pages: {
    itinerary: '',
    reportingHasParent: '',
    reportingNoParent: '',
    nonReportingHasParent: '',
    nonReportingNoParent: '',
  },
}

const toNullableInteger = value => (value === '' ? null : Number(value))

const buildFormValues = template => ({
  name: template.name || '',
  description: template.description || '',
  externalId: template.externalId || '',
  displayOrder: template.displayOrder ?? '',
  pageCount: template.pageCount ?? '',
  previewImageUrl: template.previewImageUrl || '',
  pages: {
    itinerary: template.pages?.itinerary || '',
    reportingHasParent: template.pages?.reportingHasParent || '',
    reportingNoParent: template.pages?.reportingNoParent || '',
    nonReportingHasParent: template.pages?.nonReportingHasParent || '',
    nonReportingNoParent: template.pages?.nonReportingNoParent || '',
  },
})

/**
 * Validates that an optional numeric field holds a whole number of zero or greater.
 * An empty value is allowed (these fields are optional and stored as null).
 * @param {string} value - the raw field value from the number input
 * @returns {true|string} true when valid, otherwise the error message to display
 */
const validateNonNegativeInteger = value => {
  if (value === '') {
    return true
  }
  const number = Number(value)
  return Number.isInteger(number) && number >= 0 ? true : 'Enter a whole number of 0 or greater'
}

const CreateTearsheetTemplateModal = ({
  open,
  templateType,
  templateToEdit,
  onClose,
  onSave,
  isLoading,
  isGlobal = false,
}) => {
  const { control, handleSubmit, reset, setValue, watch } = useForm({
    mode: 'onSubmit',
    defaultValues: DEFAULT_VALUES,
  })

  const { uploadFile, isUploading } = useSecureFileUpload({
    bucketType: PUBLIC_S3_BUCKET_TYPE,
    onFileUploadSuccess: ({ url }) => setValue('previewImageUrl', url),
  })

  const previewImageUrl = watch('previewImageUrl')

  useEffect(() => {
    if (templateToEdit) {
      const formValues = buildFormValues(templateToEdit)
      reset(formValues)
    }
  }, [templateToEdit, reset])

  useEffect(() => {
    if (!open) {
      reset(DEFAULT_VALUES)
    }
  }, [open])

  if (!templateType) {
    return null
  }

  const isEditing = Boolean(templateToEdit)
  const isItinerary = templateType.key === 'itinerary'
  const nodeIdFields = isItinerary ? ITINERARY_NODE_ID_FIELDS : NODE_ID_FIELDS

  const title = `${isEditing ? 'Edit' : 'Create'} ${templateType.label} Template`

  const handleClose = () => {
    onClose()
  }

  const handlePreviewImageSelect = ({ target: { files } }) => {
    const [file] = Array.from(files)
    if (file) {
      uploadFile([file])
    }
  }

  const onSubmit = data => {
    const { pages, displayOrder, pageCount, previewImageUrl: previewUrl, ...rest } = data
    const { itinerary, ...parentPages } = pages
    const payload = {
      ...rest,
      templateType: templateType.key,
      pages: isItinerary ? { itinerary } : parentPages,
    }
    if (isGlobal) {
      payload.displayOrder = toNullableInteger(displayOrder)
      payload.pageCount = toNullableInteger(pageCount)
      payload.previewImageUrl = previewUrl || null
    }
    if (isEditing) {
      payload.id = templateToEdit.id
    }
    onSave(payload)
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalTitle title={title} />
      <ModalContent>
        <Grid container flexDirection="column" rowSpacing={2}>
          <Grid item>
            <Controller
              control={control}
              name="name"
              rules={{ required: 'Name is required' }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label="Name"
                  required
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item>
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <TextField label="Description" fullWidth multiline rows={3} {...field} />
              )}
            />
          </Grid>
          <Grid item>
            <Controller
              control={control}
              name="externalId"
              rules={{ required: 'Workbook ID is required' }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label="Workbook ID"
                  required
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  {...field}
                />
              )}
            />
          </Grid>
          {isGlobal && (
            <>
              <Grid item>
                <Controller
                  control={control}
                  name="displayOrder"
                  rules={{ validate: validateNonNegativeInteger }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Display Order"
                      type="number"
                      fullWidth
                      inputProps={{ min: 0, step: 1 }}
                      error={!!error}
                      helperText={error?.message || DISPLAY_ORDER_HELPER_TEXT}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <Controller
                  control={control}
                  name="pageCount"
                  rules={{ validate: validateNonNegativeInteger }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Page Count"
                      type="number"
                      fullWidth
                      inputProps={{ min: 0, step: 1 }}
                      error={!!error}
                      helperText={error?.message || PAGE_COUNT_HELPER_TEXT}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <Typography variant="subtitle1" fontWeight="fontWeightBold" sx={{ mt: 1 }}>
                  Preview Image
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Shown as the template&apos;s preview thumbnail when users browse templates.
                </Typography>
              </Grid>
              {previewImageUrl && (
                <Grid item>
                  <Box
                    component="img"
                    src={previewImageUrl}
                    alt="Template preview"
                    sx={{ maxHeight: 160, maxWidth: '100%', objectFit: 'contain' }}
                  />
                </Grid>
              )}
              <Grid item sx={{ mb: 2 }}>
                <AttachFilesButton
                  onFileSelect={handlePreviewImageSelect}
                  disabled={isUploading}
                  text={isUploading ? 'Uploading…' : 'Upload Preview Image'}
                />
              </Grid>
            </>
          )}
          {!isItinerary && (
            <Grid item>
              <Typography variant="subtitle1" fontWeight="fontWeightBold" sx={{ mt: 1 }}>
                Node IDs
              </Typography>
            </Grid>
          )}
          {nodeIdFields.map(({ name, label }) => (
            <Grid item key={name}>
              <Controller
                control={control}
                name={name}
                rules={{ required: `${label} is required` }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label={label}
                    required
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                    {...field}
                  />
                )}
              />
            </Grid>
          ))}
        </Grid>
      </ModalContent>
      <ModalActions>
        <Button variant="text" onClick={handleClose}>
          Close
        </Button>
        <Button isLoading={isLoading} onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </ModalActions>
    </Modal>
  )
}

export default CreateTearsheetTemplateModal
